import { createContext, ReactNode, useContext, useEffect, useRef, useState, useCallback } from 'react';
import envConfig from '@/config/env';

const MAX_RETRY_ATTEMPTS = 3;
const CONNECTION_TIMEOUT = 5000;
const RECONNECTION_DELAY = 1000;
const MAX_QUEUE_SIZE = 100;
const MAX_BATCH_SIZE = 10;

type PublishEvent = 'unSubscribe' | 'subscribe';

interface WebSocketData<T> {
  data: T;
  channel: string;
}

interface IPayload {
  tokenAddress: string;
  timeframe: string;
}

const url = envConfig.VITE_CLIENT_WS_SMART_URL;

interface WebSocketContextType {
  data: WebSocketData<any> | null;
  isConnected: boolean;
  connect: () => void;
  disconnect: () => void;
  sendMessage: (event: PublishEvent, data?: any) => void;
}

const SmartWebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

export const useSmartWebSocket = (): WebSocketContextType => {
  const context = useContext(SmartWebSocketContext);
  if (!context) {
    throw new Error('useWebSocket必须在WebSocketProvider中使用');
  }
  return context;
};

export const SmartWebSocketProvider = ({ children }: { children: ReactNode }) => {
  const [data, setData] = useState<WebSocketData<any> | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  const wsRef = useRef<WebSocket | null>(null);
  const retryCount = useRef(0);
  const messageQueue = useRef<{ event: PublishEvent; data: IPayload }[]>([]);
  const isConnecting = useRef(false);

  const cleanup = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
    isConnecting.current = false;
  }, []);

  const processSendMessage = useCallback((event: PublishEvent, data: any) => {
    const params = JSON.stringify({ event, data });
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(params);
    } else if (messageQueue.current.length < MAX_QUEUE_SIZE) {
      messageQueue.current.push({ event, data });
    }
  }, []);

  const sendQueuedMessages = useCallback(() => {
    const messagesToSend = messageQueue.current.splice(0, MAX_BATCH_SIZE);
    messagesToSend.forEach((msg) => processSendMessage(msg.event, msg.data));

    if (messageQueue.current.length > 0) {
      setTimeout(sendQueuedMessages, 100);
    }
  }, [processSendMessage]);

  const connect = useCallback(() => {
    if (isConnecting.current || wsRef.current?.readyState === WebSocket.OPEN) return;
    isConnecting.current = true;

    try {
      const socket = new WebSocket(url);

      const connectionTimer = setTimeout(() => {
        if (socket.readyState !== WebSocket.OPEN) {
          socket.close();
          isConnecting.current = false;
          setIsConnected(false);
        }
      }, CONNECTION_TIMEOUT);

      socket.onopen = () => {
        clearTimeout(connectionTimer);
        setIsConnected(true);
        retryCount.current = 0;
        isConnecting.current = false;
        wsRef.current = socket;
        sendQueuedMessages();
      };

      socket.onmessage = (event) => {
        try {
          const receivedData: WebSocketData<any> = JSON.parse(event.data);
          setData(receivedData);
        } catch (err) {
          console.error('数据解析失败', err);
        }
      };

      socket.onclose = (event) => {
        const wasConnected = wsRef.current === socket;
        if (wasConnected) {
          setIsConnected(false);
          wsRef.current = null;
        }
        isConnecting.current = false;

        if (wasConnected && !event.wasClean && retryCount.current < MAX_RETRY_ATTEMPTS) {
          retryCount.current += 1;
          const delay = RECONNECTION_DELAY * Math.pow(2, retryCount.current - 1);
          setTimeout(connect, delay);
        }
      };

      socket.onerror = (event) => {
        console.error('WebSocket error', event);
        if (wsRef.current === socket) {
          cleanup();
        }
      };
    } catch (err) {
      console.error('WebSocket creation failed', err);
      isConnecting.current = false;
    }
  }, [cleanup, sendQueuedMessages]);

  const disconnect = useCallback(() => {
    retryCount.current = MAX_RETRY_ATTEMPTS;
    cleanup();
  }, [cleanup]);

  const sendMessage = useCallback(
    (event: PublishEvent, payload: any) => {
      processSendMessage(event, payload);
    },
    [processSendMessage],
  );

  useEffect(() => {
    try {
      const initTimeout = setTimeout(connect, 2000);
      return () => {
        clearTimeout(initTimeout);
        cleanup();
      };
    } catch (err) {
      console.error('WebSocket initialization failed', err);
    }
  }, [connect, cleanup]);

  return (
    <SmartWebSocketContext.Provider value={{ data: data, isConnected, connect, disconnect, sendMessage }}>{children}</SmartWebSocketContext.Provider>
  );
};
